import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { InvoiceSeriesEntity } from "containers/invoice-series/invoice-series-container.slice";
import { fetchInvoiceSeries } from "services/series.service";
import { inputCls } from "components/tw/classes";
import {
  ChangeLink,
  EditPanel,
  SettingRow,
  SettingsCard,
  StatusBanner,
  useInlineEdit,
  useSaveWithToast,
} from "./ui";

interface InvoiceSeriesCardProps {
  companyId: string;
  /** The series sales invoices number from now, or "" when none is chosen. */
  selectedSeriesId: string;
  onSelect: (seriesId: string) => Promise<unknown>;
  /** Says why a save was refused. */
  saveErrorMessage: (err: unknown) => string;
}

/**
 * Which numbering register sales invoices draw their numbers from.
 *
 * Not the same thing as the series in the document row below it: that one
 * names the PDF file, this one numbers the invoice. Picking another series here
 * is all there is to switching the format; invoices already issued keep their
 * numbers.
 */
export function InvoiceSeriesCard({
  companyId,
  selectedSeriesId,
  onSelect,
  saveErrorMessage,
}: InvoiceSeriesCardProps) {
  const { t } = useTranslation();
  const [series, setSeries] = useState<InvoiceSeriesEntity[]>([]);
  const [loadFailed, setLoadFailed] = useState(false);
  const edit = useInlineEdit(selectedSeriesId);
  const { save, saving, error } = useSaveWithToast();

  useEffect(() => {
    fetchInvoiceSeries(companyId)
      .then((payload: any) =>
        setSeries(Array.isArray(payload?.series) ? payload.series : [])
      )
      .catch(() => setLoadFailed(true));
  }, [companyId]);

  const selected = series.find((row) => row.id === selectedSeriesId);

  const saveSelection = async () => {
    const saved = await save(() => onSelect(edit.draft), {
      errorMessage: saveErrorMessage,
    });
    if (saved) edit.cancel();
  };

  return (
    <>
      {!selectedSeriesId && (
        <StatusBanner
          tone="warn"
          title={t("settings_sales_numbering_missing_title")}
          detail={t("settings_sales_numbering_missing_detail")}
          action={
            <ChangeLink
              label={t("settings_sales_numbering_pick")}
              onClick={edit.start}
            />
          }
        />
      )}
      <SettingsCard
        title={t("invoice_series_numbering")}
        description={t("settings_sales_numbering_hint")}
        actions={
          <Link
            to={`/app/${companyId}/invoice-series`}
            className="whitespace-nowrap text-sm font-medium text-indigo-600 hover:text-indigo-800"
          >
            {t("settings_sales_manage_series")}
          </Link>
        }
      >
        {loadFailed && (
          <div className="mb-3">
            <StatusBanner
              tone="error"
              title={t("invoice_series_load_failed")}
            />
          </div>
        )}
        <SettingRow
          label={t("invoice_series_active_label")}
          description={t("invoice_series_active_hint")}
          value={
            selected
              ? `${selected.label} — ${selected.title} · ${t(
                  "invoice_series_next_number"
                )}: ${selected.numberPreview ?? "—"}`
              : t("settings_none")
          }
          action={!edit.editing && <ChangeLink onClick={edit.start} />}
        >
          {edit.editing && (
            <EditPanel
              saving={saving}
              error={error}
              onSave={saveSelection}
              onCancel={edit.cancel}
            >
              <label
                htmlFor="invoice-series-in-use"
                className="mb-1 block text-sm font-medium text-gray-900"
              >
                {t("invoice_series_active_label")}
              </label>
              <select
                id="invoice-series-in-use"
                className={`${inputCls} max-w-sm`}
                value={edit.draft}
                onChange={(e) => edit.setDraft(e.target.value)}
              >
                <option value="">{t("invoice_series_choose")}</option>
                {series.map((row) => (
                  <option key={row.id} value={row.id}>
                    {`${row.label} — ${row.title}`}
                  </option>
                ))}
              </select>
              <p className="mt-2 text-xs text-gray-600">
                {t("settings_sales_series_missing")}{" "}
                <Link
                  to={`/app/${companyId}/invoice-series`}
                  className="font-medium text-indigo-600 hover:text-indigo-800"
                >
                  {t("invoice_series_create")}
                </Link>
              </p>
            </EditPanel>
          )}
        </SettingRow>
      </SettingsCard>
    </>
  );
}
